// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this
// file, exactly as StageTrendChart.tsx and SalesCharts.tsx do. The figures are computed in
// marketingData.ts, which is fully typed.
import React from 'react';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { monthLabel, monthShort } from '../../../hooks/pipelineTracker/remContract';
import { money } from './pipelineParts';
import { COST_PER_LEAD_COLOUR, channelCents, percent } from './marketingData';
import { CHART_AXIS_TICK, CHART_CURSOR, CHART_GRID, TOOLTIP_CARD, chartGradient } from './trackerKit';

/**
 * The Marketing tab's two charts. Each is a picture of the month table on the same tab, so a reader
 * who cannot see a chart, or a test that cannot render one, loses no figure.
 *
 * House chart style (trackerKit): CHART_GRID, CHART_AXIS_TICK, the dark tooltip card, each bar
 * filled with its colour's gradient. NO WHITE OR GREY HOVER BOX: recharts draws a light grey box
 * behind a hovered column by default, which the brand guidelines do not allow (founder, 5 Oct 2026),
 * so every Tooltip here passes the faint brand tint CHART_CURSOR.
 */

const TICK = CHART_AXIS_TICK;

/** A gradient id safe for any channel name: by position, never by name. */
const gradientId = (prefix, i) => `${prefix}-${i}`;

/** Exact ex GST dollars and cents, as the month table prints them. */
export const exact = (cents) =>
  (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });

const Card = ({ children }) => (
  <div className={`${TOOLTIP_CARD} min-w-[13rem]`}>{children}</div>
);

/**
 * The spend tooltip: every channel's spend that month with its share, then the month total.
 * Exported so its wording is tested without a pointer.
 */
export const SpendTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <Card>
      <p className="mb-1.5 font-bold">{row.name}</p>
      <ul className="space-y-1">
        {row.slices.map((s) => (
          <li key={s.channel} className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: s.colour }} />
            <span className="flex-1 text-gray-300">{s.channel}</span>
            <span className="tabular-nums">{exact(s.cents)}</span>
            <span className="w-9 text-right tabular-nums text-gray-400">{percent(s.share)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1.5 flex justify-between border-t border-gray-700 pt-1.5 font-semibold">
        <span>Month total</span>
        <span className="tabular-nums">{exact(row.totalCents)}</span>
      </p>
    </Card>
  );
};

/** One column per month with spend entered, stacked by channel. Values plotted in dollars. */
export const SpendByChannelChart = ({ months, channels, colours, idPrefix = 'mkt-spend' }) => {
  const rows = [...months]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((m) => {
      const row = { label: monthShort(m.month), name: monthLabel(m.month), totalCents: m.spendExGstCents };
      // Channels are keyed by position, never by name: a channel named "Google.com" would be read
      // by recharts as a path into the row.
      channels.forEach((c, i) => { row[`c${i}`] = channelCents(m, c) / 100; });
      row.slices = channels
        .map((c) => ({ channel: c, cents: channelCents(m, c), colour: colours.get(c) }))
        .filter((s) => s.cents > 0)
        .map((s) => ({ ...s, share: m.spendExGstCents > 0 ? s.cents / m.spendExGstCents : 0 }));
      return row;
    });
  return (
    <div className="h-64 w-full" role="img" aria-label="Spend by channel, by month">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} maxBarSize={56}>
          <defs>{channels.map((c, i) => chartGradient(gradientId(idPrefix, i), colours.get(c), 'donut'))}</defs>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="label" tick={TICK} stroke="#344155" tickLine={false} />
          <YAxis tick={TICK} stroke="#344155" tickLine={false} width={56} tickFormatter={(v) => money(Math.round(v * 100))} />
          <Tooltip cursor={CHART_CURSOR} content={<SpendTooltip />} />
          {channels.map((c, i) => (
            <Bar
              key={c}
              dataKey={`c${i}`}
              name={c}
              stackId="spend"
              fill={`url(#${gradientId(idPrefix, i)})`}
              isAnimationActive={false}
              radius={i === channels.length - 1 ? [4, 4, 0, 0] : 0}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const STATUS_WHY = {
  in_progress: 'Month in progress at the snapshot, so not priced',
  after_snapshot: 'After the snapshot, so not priced',
  no_snapshot: 'No Sales export yet, so not priced',
  complete: 'No leads created, so not priced',
};

export const CostPerLeadTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <Card>
      <p className="mb-1 font-bold">{row.name}</p>
      {row.cents === null ? (
        <p className="text-gray-400">{STATUS_WHY[row.status]}</p>
      ) : (
        <>
          <p style={{ color: COST_PER_LEAD_COLOUR }}>Cost per lead: <span className="tabular-nums">{exact(row.cents)}</span></p>
          <p className="text-gray-400"><span className="tabular-nums">{exact(row.spendCents)}</span> over {row.leads.toLocaleString('en-AU')} leads</p>
        </>
      )}
    </Card>
  );
};

/**
 * The average line's label, drawn at the right end ABOVE the line and inside the plot. Recharts'
 * `insideTopRight` placed it above the line too, but with no headroom, so when the average sat near
 * the top of the axis the label was clipped ("Average $72" cut off, founder 5 Oct 2026). The axis
 * now keeps headroom over the tallest bar and the label is anchored to the plot's right edge.
 */
const AverageLabel = ({ viewBox, value }) => {
  if (!viewBox) return null;
  const { x, y, width } = viewBox;
  return (
    <text x={x + width - 4} y={y - 6} textAnchor="end" fill="#cbd5e1" fontSize={11} fontWeight={600}>
      {value}
    </text>
  );
};

/** Cost per lead by month, complete months only, against the pooled average for the window. */
export const CostPerLeadChart = ({ months, averageCents, idPrefix = 'mkt-cpl' }) => {
  const rows = [...months]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((m) => ({
      label: monthShort(m.month),
      name: monthLabel(m.month),
      cpl: m.costPerLeadCents === null ? null : m.costPerLeadCents / 100,
      cents: m.costPerLeadCents,
      spendCents: m.spendExGstCents,
      leads: m.leadsCreated,
      status: m.status,
    }));
  return (
    <div className="h-56 w-full" role="img" aria-label="Cost per lead by month">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 22, right: 12, bottom: 0, left: 0 }} maxBarSize={40}>
          <defs>{chartGradient(idPrefix, COST_PER_LEAD_COLOUR, 'donut')}</defs>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="label" tick={TICK} stroke="#344155" tickLine={false} />
          <YAxis
            tick={TICK}
            stroke="#344155"
            tickLine={false}
            width={52}
            domain={[0, (dataMax) => Math.max(dataMax, (averageCents ?? 0) / 100) * 1.2]}
            tickFormatter={(v) => money(Math.round(v * 100))}
          />
          <Tooltip cursor={CHART_CURSOR} content={<CostPerLeadTooltip />} />
          {averageCents !== null && (
            <ReferenceLine
              y={averageCents / 100}
              stroke="#8892b0"
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={<AverageLabel value={`Average ${money(averageCents)}`} />}
            />
          )}
          <Bar dataKey="cpl" name="Cost per lead" fill={`url(#${idPrefix})`} radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
